import React, { useEffect, useState } from 'react';
import { biGet, today, errText } from './bi/biApi';
import { Shell, Card, Kpi, Empty, ErrorBox, selectCls } from './bi/BiUi';
import AttendanceSection from './desk/AttendanceSection';
import SubjectMatrix from './desk/SubjectMatrix';
import ExceptionsSection from './desk/ExceptionsSection';
import useDeskState from './desk/useDeskState';

const GLOBAL = ['principal', 'admin', 'admin1', 'teacher_admin_level1'];

/**
 * Unified single-screen daily workspace:
 *   A) 3-state attendance  B) topic taught + homework per subject  C) student exceptions
 * One "Save & Publish All" => POST /api/unified-daily-entry (atomic attendance + diary + homework log).
 */
export default function TeacherUnifiedDesk({ user }) {
  const isGlobal = GLOBAL.includes(user?.role);
  const [date, setDate] = useState(today());
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState(isGlobal ? '' : user?.class || user?.assignedClass || '');
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const d = useDeskState(user, classId, date);

  useEffect(() => {
    if (!isGlobal) return;
    biGet(user, '/api/classes').then((r) => setClasses([...new Set((r || []).map((c) => c.name).filter(Boolean))])).catch((e) => d.setErr(errText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGlobal]);

  const copySummary = async () => {
    try { await navigator.clipboard.writeText(d.result.whatsappSummary); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { d.setErr('Copy failed - select the text manually'); }
  };

  const q = search.toLowerCase();
  const visible = d.students.filter((s) => !q || s.name.toLowerCase().includes(q) || String(s.rollNumber).includes(q));

  return (
    <Shell>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-700/60 bg-slate-900/90 p-3 backdrop-blur">
        <div className="mr-auto">
          <h2 className="text-lg font-bold text-white">Teacher Desk</h2>
          <p className="text-xs text-slate-400">Attendance, diary, homework and remarks in one place</p>
        </div>
        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className={selectCls} aria-label="Date" />
        {isGlobal ? (
          <select value={classId} onChange={(e) => setClassId(e.target.value)} className={selectCls} aria-label="Class">
            <option value="">Select class</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : <span className="rounded-xl border border-slate-700 px-3 py-2 text-sm">Class {classId || '-'}</span>}
        <button type="button" onClick={d.save} disabled={d.saving || d.loading || !d.students.length}
          className="rounded-xl bg-gradient-to-r from-cyan-400 to-indigo-500 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg disabled:opacity-50">
          {d.saving ? 'Publishing...' : '🚀 Save & Publish All'}
        </button>
      </div>

      <ErrorBox message={d.err} />
      {d.result && (
        <Card title="✅ Published" subtitle={`${d.result.atomic ? 'Saved atomically' : 'Saved'} - diary image is generating in the background`}
          right={<button type="button" onClick={copySummary} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950">{copied ? 'Copied!' : 'Copy WhatsApp summary'}</button>}>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950/60 p-3 text-xs text-slate-300">{d.result.whatsappSummary}</pre>
        </Card>
      )}

      {!classId ? <Card><Empty>Select a class to open the workspace.</Empty></Card> : d.loading ? <Card><Empty>Loading roster...</Empty></Card> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Students" value={d.students.length} tone="indigo" icon="👥" />
            <Kpi label="Present" value={d.counts.present} tone="emerald" icon="🟢" />
            <Kpi label="Absent" value={d.counts.absent} tone="rose" icon="🔴" />
            <Kpi label="On leave" value={d.counts.leave} tone="amber" icon="🟡" />
          </div>
          <AttendanceSection students={d.students} visible={visible} att={d.att} search={search} setSearch={setSearch}
            setStatus={d.setStatus} setNote={d.setNote} markAllPresent={d.markAllPresent} />
          <SubjectMatrix subjectDefs={d.subjectDefs} subj={d.subj} activeSubject={d.activeSubject} setActiveSubject={d.setActiveSubject} setField={d.setField} />
          <ExceptionsSection students={visible} att={d.att} flags={d.flags} subjectDefs={d.subjectDefs} patchFlag={d.patchFlag} togglePending={d.togglePending} />
        </>
      )}
    </Shell>
  );
}
